import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import PhotoGallery from './PhotoGallery';
import { mediaUrl, formatBlogDate } from '../utils/blogUtils';
import { getFeaturedPost } from '../config/featuredBlogPosts';
import { getCategoryById, resolvePostBlogCategory } from '../config/blogCategories';
import { soccerPath } from '../utils/paths';
import BlogShareBar from './BlogShareBar';
import SpainArgentinaBlogArticle from './SpainArgentinaBlogArticle';
import WellnessGuideBlogArticle from './WellnessGuideBlogArticle';
import TournamentRecapBlogArticle from './TournamentRecapBlogArticle';
import TuskersLiveStatusBlogArticle from './TuskersLiveStatusBlogArticle';
import { useBlogPostMeta } from '../hooks/useBlogPostMeta';
import './Blog.css';

const URL_IN_TEXT = /(https?:\/\/[^\s]+|\/soccer\/[^\s]+)/g;

const FEATURED_ARTICLE_COMPONENTS = {
  SpainArgentinaBlogArticle,
  WellnessGuideBlogArticle,
  TournamentRecapBlogArticle,
};

const renderTextWithLinks = (text) => {
  const parts = text.split(URL_IN_TEXT);
  return parts.map((part, index) => {
    if (part.match(/^(https?:\/\/|\/soccer\/)/)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const BlogDetail = () => {
  const { slug } = useParams();
  const featuredPost = getFeaturedPost(slug);
  const FeaturedArticle = featuredPost
    ? FEATURED_ARTICLE_COMPONENTS[featuredPost.component]
    : null;
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(!featuredPost);
  const [error, setError] = useState('');

  const loadPost = useCallback(async () => {
    if (featuredPost) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await api.getPublishedPost(slug);
      setPost(data);
    } catch (err) {
      setError(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [slug, featuredPost]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  useBlogPostMeta(post || featuredPost);

  if (FeaturedArticle) {
    return <FeaturedArticle />;
  }

  if (slug === 'columbus-tuskers-live-status') {
    return <TuskersLiveStatusBlogArticle />;
  }

  if (loading) {
    return (
      <div className="blog-page">
        <p className="blog-page__status">Loading…</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-page">
        <Link to="/blog" className="blog-page__home-link">
          ← Back to blog
        </Link>
        <p className="blog-page__status blog-page__status--error">
          {error || 'Post not found'}
        </p>
      </div>
    );
  }

  const cover = post.coverImageUrl || (post.photoUrls && post.photoUrls[0]);
  const postCategory = resolvePostBlogCategory(post);
  const categoryNav = getCategoryById(postCategory);

  return (
    <article className="blog-page blog-detail">
      <header className="blog-detail__header">
        <Link to="/blog" className="blog-page__home-link">
          ← Back to blog
        </Link>
        {categoryNav && postCategory !== 'all' && (
          <Link to={categoryNav.path} className="blog-page__category-link">
            {categoryNav.label}
          </Link>
        )}
        <p className="blog-detail__meta">
          {post.clubName && (
            <Link to={soccerPath(`/clubs/${post.clubId}`)} className="blog-detail__club">
              {post.clubLogoUrl && (
                <img src={mediaUrl(post.clubLogoUrl)} alt="" className="blog-detail__club-logo" />
              )}
              {post.clubName}
            </Link>
          )}
          {post.publishedAt && <span>{formatBlogDate(post.publishedAt)}</span>}
          {post.authorName && <span>By {post.authorName}</span>}
        </p>
        <h1>{post.title}</h1>
        {post.excerpt && <p className="blog-detail__excerpt">{post.excerpt}</p>}
      </header>

      {cover && (
        <img src={mediaUrl(cover)} alt="" className="blog-detail__hero" loading="lazy" />
      )}

      <div className="blog-detail__body">
        {post.body.split('\n').map((paragraph, i) => {
          const trimmed = paragraph.trim();
          if (!trimmed) return <br key={i} />;
          return <p key={i}>{renderTextWithLinks(trimmed)}</p>;
        })}
      </div>

      <div className="blog-detail__share">
        <BlogShareBar post={post} />
      </div>

      {post.photoUrls && post.photoUrls.length > 0 && (
        <section className="blog-detail__gallery">
          <h2>Photos</h2>
          <PhotoGallery photos={post.photoUrls} />
        </section>
      )}

      {post.referenceLinks && post.referenceLinks.length > 0 && (
        <section className="blog-detail__references">
          <h2>Related links</h2>
          <ul className="blog-detail__references-list">
            {post.referenceLinks.map((link, index) => (
              <li key={`${link.url}-${index}`}>
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  🔗 {link.label || link.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
};

export default BlogDetail;
